import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { router, navigateTo } from '../services/router.js';
import * as companyApi from '../services/company-api.js';
import * as projectApi from '../services/project-api.js';
import type { Project } from '../types/project.js';
import type { Company, CompanyAgent, AgentThread, HireRequest, CompanyCeoStrategy, CompanySpecwrightProject } from '../services/company-api.js';
import '../components/company/cp-company-strategy-view.js';
import '../components/company/cp-company-team-overview.js';
import '../components/company/cp-agent-thread-panel.js';
import '../components/company/cp-company-chat.js';
import '../components/company/cp-company-budget-widget.js';
import '../components/company/cp-company-knowledge-widget.js';
import '../components/company/cp-company-drive-widget.js';
import '../components/company/cp-company-channel-widget.js';
import '../components/company/cp-company-activity-feed.js';
import '../components/company/cp-company-board.js';
import '../components/company/cp-company-content-preview.js';
import '../components/company/cp-company-projects-settings.js';
import '../components/shared/cp-confirm-dialog.js';

type Section = 'overview' | 'team' | 'board' | 'threads' | 'chat' | 'content' | 'projects';

@customElement('cp-page-company')
export class CpPageCompany extends LitElement {
  createRenderRoot() { return this; }

  @state() private _company: Company | null = null;
  @state() private _agents: CompanyAgent[] = [];
  @state() private _founderThreads: AgentThread[] = [];
  @state() private _pendingRequests: HireRequest[] = [];
  @state() private _ceoStrategies: CompanyCeoStrategy[] = [];
  @state() private _projects: CompanySpecwrightProject[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _section: Section = 'overview';
  @state() private _allProjects: Project[] = [];
  @state() private _showProjectPicker = false;
  @state() private _assigningProject = false;
  @state() private _showDeleteConfirm = false;
  @state() private _deleting = false;

  private _routeHandler = (): void => {
    this._loadForCurrentRoute();
    this.requestUpdate();
  };

  private _companyUpdatedHandler = (): void => {
    this._loadForCurrentRoute();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    router.addEventListener('route-changed', this._routeHandler);
    this.addEventListener('company-updated', this._companyUpdatedHandler);
    this._loadForCurrentRoute();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    router.removeEventListener('route-changed', this._routeHandler);
    this.removeEventListener('company-updated', this._companyUpdatedHandler);
  }

  private _loadForCurrentRoute(): void {
    const params = router.getParams();
    if (params.id) {
      void this._load(Number(params.id));
    }
  }

  private async _load(companyId: number): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const [company, agents, founderThreads, pendingRequests, ceoStrategy, projects] = await Promise.all([
        companyApi.getCompany(companyId),
        companyApi.listCompanyAgents(companyId),
        companyApi.listAgentThreads(companyId),
        companyApi.listHireRequests(companyId),
        companyApi.getCompanyCeoStrategies(companyId),
        companyApi.getProjectsByCompany(companyId),
      ]);
      this._company = company;
      this._agents = agents;
      this._founderThreads = founderThreads;
      this._pendingRequests = pendingRequests;
      this._ceoStrategies = ceoStrategy;
      this._projects = projects;
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Laden der Firma';
    } finally {
      this._loading = false;
    }
  }

  private get _ceo(): CompanyAgent | undefined {
    return this._agents.find((a) => a.role === 'ceo');
  }

  private get _activeAgents(): CompanyAgent[] {
    return this._agents.filter((a) => a.status === 'active');
  }

  private get _pendingApprovals(): HireRequest[] {
    return this._pendingRequests.filter((r) => r.status === 'pending');
  }

  private get _activeThreads(): AgentThread[] {
    return this._founderThreads.filter((t) => t.status === 'active' && t.thread_type === 'founder-to-agent');
  }

  private get _unassignedProjects(): Project[] {
    return this._allProjects.filter((p) => !p.company_id && p.status === 'active');
  }

  private async _openProjectPicker(): Promise<void> {
    this._showProjectPicker = true;
    try {
      this._allProjects = await projectApi.getProjects('active');
    } catch {
      // non-critical
    }
  }

  private async _assignProject(projectId: number): Promise<void> {
    if (!this._company) return;
    this._assigningProject = true;
    try {
      await companyApi.assignProjectToCompany(projectId, this._company.id);
      this._projects = await companyApi.getProjectsByCompany(this._company.id);
      this._allProjects = await projectApi.getProjects('active');
      if (this._unassignedProjects.length === 0) {
        this._showProjectPicker = false;
      }
    } catch {
      // error silently
    } finally {
      this._assigningProject = false;
    }
  }

  private async _unassignProject(projectId: number): Promise<void> {
    if (!this._company) return;
    try {
      await companyApi.assignProjectToCompany(projectId, null);
      this._projects = await companyApi.getProjectsByCompany(this._company.id);
    } catch {
      // error silently
    }
  }

  private _handleDelete(): void {
    this._showDeleteConfirm = true;
  }

  private async _confirmDelete(): Promise<void> {
    if (!this._company) return;
    this._deleting = true;
    try {
      await companyApi.deleteCompany(this._company.id);
      navigateTo('/companies');
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Loeschen der Firma';
    } finally {
      this._deleting = false;
      this._showDeleteConfirm = false;
    }
  }

  private _cancelDelete(): void {
    this._showDeleteConfirm = false;
  }

  private _renderOverview() {
    const company = this._company!;
    const ceo = this._ceo;

    return html`
      <div class="cp-page-company__grid">
        <div class="cp-page-company__main-col">

          <!-- CEO Strategie -->
          <section class="cp-page-company__section">
            <div class="cp-page-company__section-header">
              <h2 class="cp-page-company__section-title">CEO Strategie</h2>
              ${ceo ? html`
                <button class="cp-page-company__section-link" @click=${() => { this._section = 'chat'; }}>
                  Chat mit CEO →
                </button>
              ` : nothing}
            </div>
            <cp-company-strategy-view
              .strategies=${this._ceoStrategies}
            ></cp-company-strategy-view>
          </section>

          <!-- Aktive Threads -->
          <section class="cp-page-company__section">
            <div class="cp-page-company__section-header">
              <h2 class="cp-page-company__section-title">
                Aktive Chats
                ${this._activeThreads.length > 0 ? html`
                  <span class="cp-page-company__section-count cp-page-company__section-count--blue">${this._activeThreads.length}</span>
                ` : nothing}
              </h2>
              ${ceo ? html`
                <button class="cp-page-company__section-link" @click=${() => { this._section = 'chat'; }}>
                  Alle Threads →
                </button>
              ` : nothing}
            </div>
            ${this._activeThreads.length === 0 ? html`
              <div class="cp-page-company__empty">
                <p class="cp-page-company__empty-text">Keine aktiven Chats</p>
                ${ceo ? html`
                  <button class="cp-page-company__empty-btn" @click=${() => { this._section = 'chat'; }}>
                    Chat mit CEO starten
                  </button>
                ` : nothing}
              </div>
            ` : html`
              <div class="cp-page-company__thread-list">
                ${this._activeThreads.slice(0, 5).map((t) => html`
                  <div class="cp-page-company__thread-item" @click=${() => { this._section = 'chat'; }}>
                    <span class="cp-page-company__thread-dot"></span>
                    <span class="cp-page-company__thread-topic">${t.topic}</span>
                    <span class="cp-page-company__thread-date">
                      ${new Date(t.updated_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                `)}
              </div>
            `}
          </section>
        </div>

        <div class="cp-page-company__side-col">

          <!-- Aktivitaet -->
          <section class="cp-page-company__section">
            <h2 class="cp-page-company__section-title" style="margin-bottom:var(--spacing-md)">Aktivitaet</h2>
            <cp-company-activity-feed
              .companyId=${this._company!.id}
              .agents=${this._agents}
            ></cp-company-activity-feed>
          </section>

          <!-- Team Summary -->
          <section class="cp-page-company__section">
            <div class="cp-page-company__section-header">
              <h2 class="cp-page-company__section-title">Team</h2>
              <button class="cp-page-company__section-link" @click=${() => { this._section = 'team'; }}>
                Verwalten →
              </button>
            </div>
            ${this._agents.length === 0 ? html`
              <p class="cp-page-company__empty-text" style="text-align:center;padding:var(--spacing-md) 0;">Noch kein Team</p>
            ` : html`
              <div class="cp-page-company__team-list">
                ${this._activeAgents.slice(0, 5).map((agent) => {
                  const initials = agent.name.split(' ').filter((w: string) => w.length > 0).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();
                  return html`
                    <div class="cp-page-company__team-row">
                      <div class="cp-page-company__team-avatar" style="background-color: ${agent.color}">
                        ${initials}
                      </div>
                      <div class="cp-page-company__team-info">
                        <p class="cp-page-company__team-name">${agent.name}</p>
                        <p class="cp-page-company__team-role">${agent.role === 'ceo' ? 'CEO' : agent.role === 'department_lead' ? 'Lead' : 'Mitarbeiter'}</p>
                      </div>
                    </div>
                  `;
                })}
                ${this._agents.length > 5 ? html`
                  <p class="cp-page-company__team-more">+${this._agents.length - 5} weitere</p>
                ` : nothing}
              </div>
            `}
          </section>

          <!-- Offene Approvals -->
          ${this._pendingApprovals.length > 0 ? html`
            <section class="cp-page-company__section cp-page-company__section--amber">
              <div class="cp-page-company__section-header">
                <h2 class="cp-page-company__section-title cp-page-company__section-title--amber">
                  <span class="cp-page-company__thread-dot" style="background:var(--color-accent-warning)"></span>
                  Offene Antraege
                </h2>
                <span class="cp-page-company__section-count cp-page-company__section-count--amber">
                  ${this._pendingApprovals.length}
                </span>
              </div>
              <div class="cp-page-company__approval-list">
                ${this._pendingApprovals.slice(0, 3).map((r) => html`
                  <div class="cp-page-company__approval-card">
                    <p class="cp-page-company__approval-title">${r.template_name}${r.specialization ? ` — ${r.specialization}` : ''}</p>
                    <p class="cp-page-company__approval-reason">${r.reason}</p>
                  </div>
                `)}
              </div>
              <button class="cp-page-company__section-link cp-page-company__section-link--amber" @click=${() => { this._section = 'team'; }}>
                Alle Antraege bearbeiten →
              </button>
            </section>
          ` : nothing}

          <!-- Info -->
          <section class="cp-page-company__section">
            <h2 class="cp-page-company__section-title" style="margin-bottom:var(--spacing-sm)">Info</h2>
            <dl class="cp-page-company__info-list">
              <div class="cp-page-company__info-row">
                <dt class="cp-page-company__info-label">Typ</dt>
                <dd class="cp-page-company__info-value">${company.type || '–'}</dd>
              </div>
              <div class="cp-page-company__info-row">
                <dt class="cp-page-company__info-label">Status</dt>
                <dd>
                  <span class="cp-page-company__status-badge ${company.status === 'active' ? 'cp-page-company__status-badge--active' : 'cp-page-company__status-badge--deactivated'}">
                    ${company.status === 'active' ? 'Aktiv' : 'Deaktiviert'}
                  </span>
                </dd>
              </div>
              <div class="cp-page-company__info-row">
                <dt class="cp-page-company__info-label">Mitglieder</dt>
                <dd class="cp-page-company__info-value">${this._activeAgents.length}</dd>
              </div>
              <div class="cp-page-company__info-row">
                <dt class="cp-page-company__info-label">Gegruendet</dt>
                <dd class="cp-page-company__info-value">${new Date(company.created_at).toLocaleDateString('de-DE')}</dd>
              </div>
            </dl>
            ${!company.is_meta ? html`
              <button
                class="cp-page-company__delete-btn"
                @click=${() => this._handleDelete()}
                ?disabled=${this._deleting}
              >
                Firma loeschen
              </button>
            ` : nothing}
          </section>

          <!-- Projekte -->
          <section class="cp-page-company__section">
            <div class="cp-page-company__section-header">
              <h2 class="cp-page-company__section-title">Projekte</h2>
              <button class="cp-page-company__section-link" @click=${() => this._openProjectPicker()}>
                + Zuweisen
              </button>
            </div>

            ${this._showProjectPicker ? html`
              <div class="cp-page-company__project-picker">
                ${this._unassignedProjects.length === 0 ? html`
                  <p class="cp-page-company__empty-text" style="font-size:var(--font-size-xs)">Keine verfuegbaren Projekte</p>
                ` : html`
                  ${this._unassignedProjects.map((p) => html`
                    <div class="cp-page-company__project-picker-item">
                      <div class="cp-page-company__project-info">
                        <p class="cp-page-company__project-name">${p.name}</p>
                        <p class="cp-page-company__project-path">${p.path}</p>
                      </div>
                      <button
                        class="cp-page-company__project-assign-btn"
                        @click=${() => this._assignProject(p.id)}
                        ?disabled=${this._assigningProject}
                      >
                        Zuweisen
                      </button>
                    </div>
                  `)}
                `}
                <button
                  class="cp-page-company__project-picker-close"
                  @click=${() => { this._showProjectPicker = false; }}
                >
                  Schliessen
                </button>
              </div>
            ` : nothing}

            ${this._projects.length === 0 && !this._showProjectPicker ? html`
              <p class="cp-page-company__empty-text" style="text-align:center;padding:var(--spacing-md) 0;">Keine Projekte zugeordnet</p>
            ` : nothing}

            ${this._projects.length > 0 ? html`
              <div class="cp-page-company__project-list">
                ${this._projects.map((p) => html`
                  <div class="cp-page-company__project-item">
                    <span class="cp-page-company__project-icon">📁</span>
                    <div class="cp-page-company__project-info">
                      <p class="cp-page-company__project-name">${p.name}</p>
                      <p class="cp-page-company__project-path">${p.path}</p>
                    </div>
                    <button
                      class="cp-page-company__project-remove-btn"
                      @click=${() => this._unassignProject(p.id)}
                      title="Zuordnung entfernen"
                    >
                      ✕
                    </button>
                  </div>
                `)}
              </div>
            ` : nothing}
          </section>

          <!-- Firmen-Wissen -->
          <section class="cp-page-company__section">
            <h2 class="cp-page-company__section-title" style="margin-bottom:var(--spacing-md)">Firmen-Wissen</h2>
            <cp-company-knowledge-widget
              .companyId=${this._company!.id}
            ></cp-company-knowledge-widget>
          </section>

          <!-- Messaging Bots -->
          <section class="cp-page-company__section">
            <h2 class="cp-page-company__section-title" style="margin-bottom:var(--spacing-md)">Messaging Bots</h2>
            <cp-company-channel-widget
              .companyId=${this._company!.id}
            ></cp-company-channel-widget>
          </section>

          <!-- Google Drive -->
          <section class="cp-page-company__section">
            <h2 class="cp-page-company__section-title" style="margin-bottom:var(--spacing-md)">Google Drive</h2>
            <cp-company-drive-widget
              .companyId=${this._company!.id}
              .driveFolderId=${this._company!.drive_folder_id}
              .driveAccount=${this._company!.drive_account}
              .driveFolderName=${this._company!.drive_folder_name}
            ></cp-company-drive-widget>
          </section>

          <!-- Budget -->
          <section class="cp-page-company__section">
            <h2 class="cp-page-company__section-title" style="margin-bottom:var(--spacing-md)">Budget</h2>
            <cp-company-budget-widget
              .companyId=${this._company!.id}
              .agents=${this._agents}
            ></cp-company-budget-widget>
          </section>

          <!-- Agent-Kommunikation -->
          <section class="cp-page-company__section">
            <div class="cp-page-company__section-header">
              <h2 class="cp-page-company__section-title">Agent-Kommunikation</h2>
              <button class="cp-page-company__section-link" @click=${() => { this._section = 'threads'; }}>
                Alle →
              </button>
            </div>
            <cp-agent-thread-panel
              .companyId=${this._company!.id}
              .agents=${this._agents}
            ></cp-agent-thread-panel>
          </section>
        </div>
      </div>
    `;
  }

  private _renderNavTab(section: Section, label: string) {
    return html`
      <button
        @click=${() => { this._section = section; }}
        class="cp-page-company__nav-btn ${this._section === section ? 'cp-page-company__nav-btn--active' : ''}"
      >
        ${label}
      </button>
    `;
  }

  override render() {
    if (this._loading) {
      return html`
        <div class="cp-page">
          <div class="cp-page-company__loading">Firma wird geladen...</div>
        </div>
      `;
    }

    if (this._error) {
      return html`
        <div class="cp-page">
          <div class="cp-page-company__error">${this._error}</div>
        </div>
      `;
    }

    if (!this._company) return nothing;

    const company = this._company;
    const ceo = this._ceo;
    const initials = company.name.split(' ').filter((w: string) => w.length > 0).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();

    return html`
      <div class="cp-page">
        <!-- Header -->
        <div>
          <button class="cp-page-company__back-btn" @click=${() => navigateTo('/companies')}>
            ← Firmen
          </button>
          <div class="cp-page-company__header-row">
            <div
              class="cp-page-company__avatar"
              style="background-color: ${company.color ?? '#6B7280'}"
            >
              ${company.icon ?? initials}
            </div>
            <div class="cp-page-company__header-info">
              <h1 class="cp-page__title">${company.name}</h1>
              ${company.type ? html`<p class="cp-page-company__subtitle">${company.type}</p>` : nothing}
            </div>
          </div>
        </div>

        <!-- Tab Navigation -->
        <div class="cp-page-company__nav">
          ${this._renderNavTab('overview', 'Uebersicht')}
          ${this._renderNavTab('board', 'Board')}
          ${this._renderNavTab('content', 'Content')}
          ${this._renderNavTab('team', 'Team')}
          ${this._renderNavTab('threads', 'Agent-Kommunikation')}
          ${ceo ? this._renderNavTab('chat', 'Chat') : nothing}
          ${this._renderNavTab('projects', 'Projekte')}
        </div>

        <div class="cp-page__content">
          ${this._section === 'overview' ? this._renderOverview() : nothing}

          ${this._section === 'board' ? html`
            <cp-company-board
              .companyId=${company.id}
            ></cp-company-board>
          ` : nothing}

          ${this._section === 'content' ? html`
            <cp-company-content-preview
              .companyId=${company.id}
            ></cp-company-content-preview>
          ` : nothing}

          ${this._section === 'team' ? html`
            <cp-company-team-overview
              .companyId=${company.id}
              .ceoAgentId=${ceo?.id ?? 0}
              .companyType=${company.type ?? ''}
            ></cp-company-team-overview>
          ` : nothing}

          ${this._section === 'threads' ? html`
            <cp-agent-thread-panel
              .companyId=${company.id}
              .agents=${this._agents}
            ></cp-agent-thread-panel>
          ` : nothing}

          ${this._section === 'chat' && ceo ? html`
            <cp-company-chat
              .companyId=${company.id}
              .agentId=${ceo.id}
              .companyName=${company.name}
            ></cp-company-chat>
          ` : nothing}

          ${this._section === 'projects' ? html`
            <cp-company-projects-settings
              .companyId=${company.id}
            ></cp-company-projects-settings>
          ` : nothing}
        </div>

        <cp-confirm-dialog
          .open=${this._showDeleteConfirm}
          title="Firma loeschen"
          message="Firma und alle zugehoerigen Daten (Agents, Threads, Strategien, Tickets) werden unwiderruflich geloescht."
          confirmLabel="Endgueltig loeschen"
          variant="danger"
          @dialog-confirmed=${this._confirmDelete}
          @dialog-cancelled=${this._cancelDelete}
        ></cp-confirm-dialog>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-company': CpPageCompany;
  }
}
